import type { Book, Chapter } from '../store/booksStore'
import type {
  SupportedWorkbenchBuildDataset,
  WorkbenchBuildTargetRecord,
  WorkbenchProjectRecord
} from '../types/workbenchProjects'
import { compareWorkbenchText, workbenchSafeText } from './workbenchSafeValue'

interface BuildDatasetDefinition {
  dataset_name: SupportedWorkbenchBuildDataset
  matchesContext: (chapter: Chapter) => boolean
  supportedWitnesses: Array<{
    sigla: string
    aliases: string[]
  }>
}

const BUILD_DATASET_DEFINITIONS: BuildDatasetDefinition[] = [
  {
    dataset_name: 'rd_p8v3',
    matchesContext: chapter => workbenchSafeText(chapter.name).toLowerCase() === 'rd',
    supportedWitnesses: [
      { sigla: 'Rd-Met-1981', aliases: ['Rd-Met-1981', 'Met 1981', 'A'] },
      { sigla: 'Rd-Montreal-117953', aliases: ['Rd-Montreal-117953', 'Montreal', 'B'] },
      { sigla: 'Rd-München-616', aliases: ['Rd-München-616', 'München 616', 'C'] },
      { sigla: 'Rd-Paris-3465', aliases: ['Rd-Paris-3465', 'Paris 3465', 'D'] },
      { sigla: 'Rd-Paris-3466', aliases: ['Rd-Paris-3466', 'Paris 3466', 'E'] },
      { sigla: 'Rd-Paris-3471', aliases: ['Rd-Paris-3471', 'Paris 3471', 'F'] },
      { sigla: 'Rd-Parker-578', aliases: ['Rd-Parker-578', 'Parker 578', 'G'] },
      { sigla: 'Rd-Rabat-3655', aliases: ['Rd-Rabat-3655', 'Rabat 3655', 'H'] },
      { sigla: 'Rd-Riyadh-2536', aliases: ['Rd-Riyadh-2536', 'Riyadh 2536', 'I'] }
    ]
  }
]

const isSupportedWorkbenchBuildDataset = (value: unknown): value is SupportedWorkbenchBuildDataset => (
  value === 'rd_p8v3'
)

const sanitizeWorkbenchBuildTarget = (
  target: Partial<WorkbenchBuildTargetRecord> | null | undefined
): WorkbenchBuildTargetRecord | null => {
  const workId = workbenchSafeText(target?.work_id)
  const contextId = workbenchSafeText(target?.context_id)
  const datasetName = workbenchSafeText(target?.dataset_name)

  if (!workId || !contextId || !isSupportedWorkbenchBuildDataset(datasetName)) {
    return null
  }

  return {
    work_id: workId,
    context_id: contextId,
    dataset_name: datasetName
  }
}

export const inferWorkbenchBuildTargets = ({
  books,
  chapters,
  sourceBookIds
}: {
  books: Book[]
  chapters: Chapter[]
  sourceBookIds: string[]
}): WorkbenchBuildTargetRecord[] => {
  const scopedBookIds = new Set(sourceBookIds)

  return chapters
    .filter(chapter => scopedBookIds.has(chapter.book_id))
    .flatMap(chapter => BUILD_DATASET_DEFINITIONS
      .filter(definition => definition.matchesContext(chapter))
      .map(definition => ({
        work_id: workbenchSafeText(chapter.book_id),
        context_id: workbenchSafeText(chapter.id),
        dataset_name: definition.dataset_name
      })))
    .sort((left, right) => (
      compareWorkbenchText(left.work_id, right.work_id)
      || compareWorkbenchText(left.context_id, right.context_id)
    ))
}

export const getEffectiveWorkbenchBuildTargets = ({
  project,
  books,
  chapters,
  sourceBookIds
}: {
  project?: WorkbenchProjectRecord | null
  books: Book[]
  chapters: Chapter[]
  sourceBookIds?: string[]
}): WorkbenchBuildTargetRecord[] => {
  const rawBuildTargets = project?.build_targets
  if (Array.isArray(rawBuildTargets)) {
    const sanitizedTargets = rawBuildTargets
      .map(sanitizeWorkbenchBuildTarget)
      .filter((target): target is WorkbenchBuildTargetRecord => Boolean(target))

    if (sanitizedTargets.length !== rawBuildTargets.length) {
      console.warn('[Workbench] Ignored malformed build targets on project record.', {
        projectId: workbenchSafeText(project?.id, 'unknown-project'),
        originalCount: rawBuildTargets.length,
        sanitizedCount: sanitizedTargets.length
      })
    }

    return sanitizedTargets
      .sort((left, right) => (
        compareWorkbenchText(left.work_id, right.work_id)
        || compareWorkbenchText(left.context_id, right.context_id)
      ))
  }

  const rawSourceBookIds = project?.source_book_ids
  const effectiveSourceBookIds = Array.isArray(rawSourceBookIds) && rawSourceBookIds.length
    ? rawSourceBookIds.map(bookId => workbenchSafeText(bookId)).filter(Boolean)
    : (sourceBookIds || [])

  return inferWorkbenchBuildTargets({
    books,
    chapters,
    sourceBookIds: effectiveSourceBookIds
  })
}

export const resolveWorkbenchBuildDataset = ({
  project,
  books,
  chapters,
  sourceBookIds,
  workId,
  contextId
}: {
  project?: WorkbenchProjectRecord | null
  books: Book[]
  chapters: Chapter[]
  sourceBookIds?: string[]
  workId?: string | null
  contextId?: string | null
}): SupportedWorkbenchBuildDataset | null => {
  if (!workId) {
    return null
  }
  if (!contextId) {
    return null
  }

  const explicitTargets = getEffectiveWorkbenchBuildTargets({
    project,
    books,
    chapters,
    sourceBookIds
  })
  const explicitTarget = explicitTargets
    .find(target => target.work_id === workId && target.context_id === contextId)
  if (explicitTarget) {
    return explicitTarget.dataset_name
  }

  return null
}

export const getSupportedWorkbenchBuildWitnessSigla = (
  datasetName: SupportedWorkbenchBuildDataset | null | undefined
): string[] => {
  if (!datasetName) {
    return []
  }

  return BUILD_DATASET_DEFINITIONS
    .find(definition => definition.dataset_name === datasetName)
    ?.supportedWitnesses.map(witness => witness.sigla)
    ?? []
}

export const getSupportedWorkbenchBuildWitnessOptions = (
  datasetName: SupportedWorkbenchBuildDataset | null | undefined
): Array<{ sigla: string; aliases: string[] }> => {
  if (!datasetName) {
    return []
  }

  return BUILD_DATASET_DEFINITIONS
    .find(definition => definition.dataset_name === datasetName)
    ?.supportedWitnesses.map(witness => ({
      sigla: witness.sigla,
      aliases: [...witness.aliases]
    }))
    ?? []
}
